import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
	DocumentArrowDownIcon,
	FunnelIcon,
} from '@heroicons/react/20/solid';

import { ContainerFull } from '../../components/ContainerFull';
import { Heading } from '../../components/Heading';
import { Wrapper } from '../../components/Wrapper';
import { Button } from '../../components/buttons/Button';
import { PopoverCheckboxFilter } from '../../components/popoverCheckboxFilter/PopoverCheckboxFilter';
import { PopoverRangeDate } from '../../components/popoverRangeDate/PopoverRangeDate';

import { SmartTable } from '../../components/smartTable/SmartTable';
import { Tooltip } from '../../components/smartTable/Tooltip';
import { useTablePrefs } from '../../hooks/useTablePrefs';

import { getAllStudents } from '../../redux/actions/users';
import { verifyStateApp } from '../../redux/actions/stateApp';
import { exportToExcel } from '../../common/importDataToExcel';
import { statusStudentsFilter, statusSubscriptionFilter, NOTE_STATUS_OPTIONS } from '../../static/data';

import { DrawerProfileUser } from '../profile/drawerProfileUser/DrawerProfileUser';
import { DrawerFiltersStudents } from './DrawerFiltersStudents';
import { buildStudentsColumns } from './students.columns';
import { getOrderedExcelHeadersForExport } from '../../common/getOrderedExcelHeadersForExport';

const TABLE_KEY = 'alumnos';

const EMPTY_FILTERS = {
	statusStudent: [],
	statusSubscription: [],
	noteStatus: [],
	fromDate: '',
	toDate: '',
};

const DEFAULT_PREFS = {
	query: {
		page: 0,
		pageSize: 15,
		sorting: [{ id: 'createdAt', desc: true }],
		filters: { ...EMPTY_FILTERS },
		columnSearch: {},
	},
	view: {
		columnVisibility: {},
		columnOrder: [],
		columnSizing: {},
	},
};

/** Placeholders mientras `useTablePrefs` termina el GET remoto (evita parpadeo de filtros). */
const StudentsFilterBarSkeleton = () => (
	<div className='hidden w-full flex-wrap items-center justify-end gap-3 md:flex md:flex-1'>
		{[1, 2, 3, 4].map((i) => (
			<div
				key={i}
				className='h-10 w-44 max-w-[40vw] shrink-0 rounded-md bg-slate-200/90 animate-blink'
				aria-hidden
			/>
		))}
	</div>
);

const sortingToApi = (sorting) => {
	if (!Array.isArray(sorting) || sorting.length === 0) {
		return { field: 'createdAt', dir: 'desc' };
	}
	const [first] = sorting;
	return { field: first.id, dir: first.desc ? 'desc' : 'asc' };
};

const buildApiPayload = (query) => ({
	page: query.page,
	pageSize: query.pageSize,
	filters: query.filters || {},
	columnSearch: query.columnSearch || {},
	sort: sortingToApi(query.sorting),
	usePagination: true,
});

export const StudentsPage = () => {
	const dispatch = useDispatch();
	const { students, loading } = useSelector((state) => state.users);

	const [prefs, , patchPrefs, resetPrefs, prefsHydrated] = useTablePrefs(TABLE_KEY, DEFAULT_PREFS);

	const [totalRows, setTotalRows] = useState(0);
	const [isOpenDrawerProfile, setIsOpenDrawerProfile] = useState(false);
	const [userIdSelected, setUserIdSelected] = useState('');
	const [isOpenFiltersDrawer, setIsOpenFiltersDrawer] = useState(false);
	const [exportExcelLoading, setExportExcelLoading] = useState(false);

	// `formData` es el shape plano que esperan los popovers existentes.
	// Debe reflejar `prefs.query.filters` siempre: al hidratar (sessionStorage/backend) o cuando
	// cambia el usuario la clave de storage; si solo inicializamos useState en el mount, la
	// barra queda en vacío mientras la tabla ya filtra.
	const [formData, setFormData] = useState(() => ({ ...EMPTY_FILTERS, ...prefs.query.filters }));
	const [mobileFormData, setMobileFormData] = useState(formData);

	const filtersSyncKey = JSON.stringify(prefs.query.filters ?? {});
	useEffect(() => {
		setFormData({ ...EMPTY_FILTERS, ...prefs.query.filters });
	}, [filtersSyncKey]);

	const patchQuery = useCallback((patch) => {
		patchPrefs({ query: { ...prefs.query, ...patch } });
	}, [patchPrefs, prefs.query]);

	const patchView = useCallback((patch) => {
		patchPrefs({ view: { ...prefs.view, ...patch } });
	}, [patchPrefs, prefs.view]);

	const handleColumnSearchChange = useCallback((columnId, value) => {
		const current = prefs.query.columnSearch || {};
		const trimmed = (value || '').trim();
		const next = { ...current };
		if (trimmed) {
			if (current[columnId] === trimmed) return;
			next[columnId] = trimmed;
		} else {
			if (!(columnId in current)) return;
			delete next[columnId];
		}
		patchQuery({ page: 0, columnSearch: next });
	}, [patchQuery, prefs.query.columnSearch]);

	const handleOpenProfile = useCallback((userId) => {
		setUserIdSelected(userId);
		setIsOpenDrawerProfile(true);
	}, []);

	const toggleDrawerProfile = useCallback(() => {
		setIsOpenDrawerProfile((p) => !p);
	}, []);

	const toggleFiltersDrawer = useCallback(() => {
		setIsOpenFiltersDrawer((prev) => {
			const next = !prev;
			if (next) setMobileFormData(formData);
			return next;
		});
	}, [formData]);

	const columns = useMemo(
		() => buildStudentsColumns({ onOpenProfile: handleOpenProfile }),
		[handleOpenProfile],
	);

	// Solo refetcheamos cuando cambia el slice "query" (no las preferencias visuales),
	// y solo despues de hidratar prefs remotas para no pedir datos con config provisional.
	const queryKey = JSON.stringify(prefs.query);
	useEffect(() => {
		if (!prefsHydrated) return;
		dispatch(getAllStudents(buildApiPayload(prefs.query))).then((result) => {
			if (result?.status === 200) {
				setTotalRows(result.totalRows || 0);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey, dispatch, prefsHydrated]);

	const applyFilters = (nextFormData) => {
		const data = nextFormData || formData;
		patchQuery({
			page: 0,
			filters: {
				statusStudent: data.statusStudent || [],
				statusSubscription: data.statusSubscription || [],
				noteStatus: data.noteStatus || [],
				fromDate: data.fromDate || '',
				toDate: data.toDate || '',
			},
		});
	};

	const removeFilters = () => {
		setFormData({ ...EMPTY_FILTERS });
		patchQuery({
			page: 0,
			filters: { ...EMPTY_FILTERS },
			columnSearch: {},
		});
	};

	const applyMobileFilters = () => {
		setFormData(mobileFormData);
		applyFilters(mobileFormData);
		setIsOpenFiltersDrawer(false);
	};

	const clearMobileFilters = () => {
		setMobileFormData({ ...EMPTY_FILTERS });
		removeFilters();
	};

	const handleImportData = async (name) => {
		const stateAppRes = await dispatch(verifyStateApp());
		if (stateAppRes?.data === false) {
			toast.error(stateAppRes.message);
			return;
		}
		const excelColumns = getOrderedExcelHeadersForExport(columns, prefs.view);
		if (excelColumns.length === 0) {
			toast.error('No hay columnas de datos para exportar. Muestra al menos una columna exportable en la tabla.');
			return;
		}

		setExportExcelLoading(true);
		const loadingToastId = toast.loading('Generando...');
		try {
			const result = await dispatch(getAllStudents({
				...buildApiPayload(prefs.query),
				usePagination: false,
				sendExcel: true,
				excelColumns,
			}));
			if (result?.status === 200) {
				const response = exportToExcel(result.excelData, name);
				if (response.data) {
					toast.error(response.message);
				} else {
					toast.success(response.message);
				}
			} else {
				toast.error(result?.message || 'No se pudo generar el archivo Excel.');
			}
		} catch {
			toast.error('Error al generar el archivo Excel.');
		} finally {
			toast.dismiss(loadingToastId);
			setExportExcelLoading(false);
		}
	};

	const rows = students;

	return (
		<ContainerFull>
			<Heading
				title='Lista de estudiantes'
				subtitle='Muestra la lista de todos los estudiantes registrados en la plataforma.'
				center={false}
			/>

			<div className='mt-4 flex w-full flex-col items-center justify-end gap-4 md:flex-row'>
				<div className='flex w-full md:hidden'>
					<Button
						onClick={toggleFiltersDrawer}
						disabled={!prefsHydrated}
						icon={<FunnelIcon className='block h-5 w-5' aria-hidden='true' />}
						variant='barFilterSecondary'
						className='w-full'
					>
						Filtros
					</Button>
				</div>

				{prefsHydrated ? (
					<>
						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Filtrar por estatus de suscripción' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverCheckboxFilter
										state={formData}
										setState={setFormData}
										property='statusSubscription'
										options={statusSubscriptionFilter}
										title='Estatus de suscripción'
										subtitle='Filtra estudiantes por su estado actual de suscripción.'
										placeholderLabel='Estatus de suscripción'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Filtrar por estatus de estudiante' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverCheckboxFilter
										state={formData}
										setState={setFormData}
										property='statusStudent'
										options={statusStudentsFilter}
										title='Estatus de estudiante'
										subtitle='Filtra estudiantes por su estado en la plataforma.'
										placeholderLabel='Estatus de estudiante'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Filtrar por estatus de notas' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverCheckboxFilter
										state={formData}
										setState={setFormData}
										property='noteStatus'
										options={NOTE_STATUS_OPTIONS}
										title='Estatus de notas'
										subtitle='Estudiantes con al menos una nota en los estatus seleccionados.'
										placeholderLabel='Estatus de notas'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip
								title='Filtrar por fecha de vencimiento de la suscripción (rango)'
								placement='top'
							>
								<div className='w-full md:w-auto'>
									<PopoverRangeDate
										state={formData}
										setState={setFormData}
										properties={['fromDate', 'toDate']}
										popoverTitle='Fecha de vencimiento de suscripción'
										popoverSubtitle='Filtra estudiantes según la fecha límite de pago de su suscripción (vencimiento).'
										emptySummaryLabel='Vencimiento de suscripción'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>
					</>
				) : (
					<StudentsFilterBarSkeleton />
				)}

				<div className='flex w-full flex-col items-stretch gap-1 md:w-auto md:items-end'>
					<Tooltip
						title={exportExcelLoading ? 'Generando archivo Excel…' : 'Descargar archivo de Excel'}
						placement='top'
					>
						<Button
							onClick={() => handleImportData('Lista_de_estudiantes.xlsx')}
							disabled={!prefsHydrated || exportExcelLoading}
							icon={<DocumentArrowDownIcon className='block h-5 w-5' aria-hidden='true' />}
							variant='barFilterSecondary'
							className='w-full md:w-auto'
							loading={exportExcelLoading}
							loadingLabel='Generando...'
						>
							Exportar lista
						</Button>
					</Tooltip>
				</div>
			</div>

			<Wrapper>
				<SmartTable
					data={rows}
					columns={columns}
					totalRows={totalRows}
					query={prefs.query}
					onQueryChange={patchQuery}
					onColumnSearchChange={handleColumnSearchChange}
					view={prefs.view}
					onViewChange={patchView}
					loading={loading}
					preferencesReady={prefsHydrated}
					emptyMessage='No hay registro de estudiantes'
					pageSizeOptions={[10, 15, 25, 50]}
					onResetPrefs={resetPrefs}
					getRowId={(row) => row.id}
				/>
			</Wrapper>

			<DrawerProfileUser
				isOpenDrawerProfile={isOpenDrawerProfile}
				toggleDrawerProfile={toggleDrawerProfile}
				userId={userIdSelected}
				onUserUpdated={() => {
					dispatch(getAllStudents(buildApiPayload(prefs.query))).then((result) => {
						if (result?.status === 200) setTotalRows(result.totalRows || 0);
					});
				}}
				refreshTable={true}
			/>
			<DrawerFiltersStudents
				isOpen={isOpenFiltersDrawer}
				toggleDrawer={toggleFiltersDrawer}
				formData={mobileFormData}
				setFormData={setMobileFormData}
				onClear={clearMobileFilters}
				onApply={applyMobileFilters}
			/>
		</ContainerFull>
	);
};
